import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

import { getToken } from './token-storage';

// Use Vite's import.meta.env instead of process.env for browser compatibility
const BASE_URL = import.meta.env.VITE_API_URL || window.location.origin;

// Funzione per ottenere opzioni di richiesta con header di autorizzazione JWT
export function getAuthHeaders(): Record<string, string> {
  let headers: Record<string, string> = {};

  try {
    // Ottieni il token dal token storage
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch (error) {
    console.error('Errore durante il recupero del token JWT:', error);
  }

  return headers;
}

export const apiRequest = async (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>
): Promise<any> => {
  const token = getToken();

  // Le chiamate di autenticazione non richiedono un token
  const isAuthEndpoint = url.includes('/api/auth/');
  if (!token && !isAuthEndpoint) {
    throw new Error('No authentication token available');
  }

  // Add cache-busting for validation-related requests
  const isValidationRequest = url.includes('/validations') || url.includes('/users/me');
  const cacheBustingHeaders = isValidationRequest ? {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
    'X-Requested-With': 'XMLHttpRequest',
    'X-Cache-Bust': Date.now().toString()
  } : {};

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...cacheBustingHeaders,
  };

  const config: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };

  // Ensure endpoint starts with /
  const normalizedEndpoint = url.startsWith('/') ? url : `/${url}`;
  const fullUrl = `${BASE_URL}${normalizedEndpoint}`;

  if (data && method !== 'GET') {
    config.body = JSON.stringify(data);
  }

  // Timeout di 20 secondi per gestire meglio le richieste lente
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);
  config.signal = controller.signal;

  try {
    const response = await fetch(fullUrl, config);
    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout - please try again');
    }
    throw error;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleResponse(response: Response): Promise<any> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return await response.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Ottieni gli header di autorizzazione JWT
    const authHeaders = getAuthHeaders();

    const res = await fetch(queryKey[0] as string, {
      headers: authHeaders,
      credentials: "include", // manteniamo il supporto per i cookie per retrocompatibilità
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      retry: 1,
      staleTime: 5 * 60 * 1000,  // 5 minutes - data considered fresh for 5 min
      gcTime: 10 * 60 * 1000,    // 10 minutes - cache kept in memory for 10 min
    },
  },
});